import { ComponentType, FocusEventHandler, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormFieldTooltip, FormFieldTooltipProps } from './tooltip';

export interface ErrorTooltipParams {
  /**
   * If this value is true, the error will always displayed to the user
   * Otherwise, the error will be displayed when the user focuses on the field.
   */
  alwaysView?: boolean;
  tooltipProps?: Omit<FormFieldTooltipProps, 'message' | 'onClose' | 'open' | 'children'>;
}

const defaultValue: ErrorTooltipParams = { alwaysView: true };

export const withErrorTooltip =
  <T extends ErrorTooltipParams>(Component: ComponentType<any>, params?: T): ComponentType<any> =>
  ({ onFocus, name, ...props }: any) => {
    const { alwaysView, tooltipProps } = { ...defaultValue, ...params };
    const { getFieldState } = useFormContext();
    const { invalid, error } = name ? getFieldState(name) : { invalid: true, error: { message: 'Missed `name` prop' } };
    const [open, setOpen] = useState(alwaysView ?? invalid);

    const handleFocus: FocusEventHandler<any> = useCallback(
      (e) => {
        if (alwaysView) {
          return;
        }
        setOpen(invalid);
        onFocus?.(e);
      },
      [alwaysView, invalid, onFocus],
    );

    const handleClose = useCallback(() => {
      if (alwaysView) {
        return;
      }
      setOpen(false);
    }, [alwaysView, setOpen]);

    return (
      <FormFieldTooltip
        colorSchema="error"
        {...tooltipProps}
        message={error?.message}
        onClose={handleClose}
        open={Boolean(open && error?.message)}
      >
        <div data-testid="withErrorTooltip" style={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column' }}>
          <Component {...props} invalid={invalid} name={name} onFocus={handleFocus} />
        </div>
      </FormFieldTooltip>
    );
  };
